import { useCallback, useEffect, useState } from 'react';

import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';

/** A map centre in the shape the shared geolocation state stores coordinates in. */
export interface SearchCenter {
  lat: number;
  lng: number;
}

interface UseSearchThisAreaArgs {
  /** Pane-specific work once the search is committed, e.g. clearing the field it replaces. */
  onSearch: () => void;
}

interface UseSearchThisArea {
  /** True once the guest has moved the map away from the location the results are for. */
  canSearchThisArea: boolean;
  /** Hand to the map's user-move signal. */
  onMapMovedByUser: () => void;
  searchArea: (center: SearchCenter | null) => void;
}

/**
 * Backs the "search this area" button: tracks whether the map has been moved away from the
 * location the current results belong to, and searches a new one when asked.
 *
 * Searching writes the map centre as the manual coordinates, which is all it takes — the
 * nearby query already fetches around those, and keeps the radius a location search uses.
 * The area on screen is deliberately not consulted for the radius, so a store that a
 * location search would return is a store this button returns as well.
 */
export function useSearchThisArea({ onSearch }: UseSearchThisAreaArgs): UseSearchThisArea {
  const { activeCoordinates, setCoordinatesManual, setManualAddress } = useGeolocation();
  const [hasMovedMap, setHasMovedMap] = useState(false);

  // The results follow the searched location, so anything that moves it — locate-me, an
  // address search, this button — leaves the map showing the area it already searched.
  useEffect(() => {
    setHasMovedMap(false);
  }, [activeCoordinates]);

  const onMapMovedByUser = useCallback(() => setHasMovedMap(true), []);

  const searchArea = useCallback(
    (center: SearchCenter | null) => {
      if (!center) {
        return;
      }

      setHasMovedMap(false);
      setCoordinatesManual({ newCoords: center });
      // Whatever address is in the field describes the area the guest has just left.
      setManualAddress('');
      onSearch();
    },
    [onSearch, setCoordinatesManual, setManualAddress]
  );

  return { canSearchThisArea: hasMovedMap, onMapMovedByUser, searchArea };
}
